<template>
  <div class="modal-card">
    <b-loading :isFullPage="false" :active="isLoading" />
    <header class="modal-card-head">
      <span class="modal-card-title is-size-5 has-text-centered">{{
        unarchived ? General.UNARCHIVE : General.ARCHIVE
      }}</span>
      <div class="close-button cursor-pointer" @click.stop="$emit('close')">
        <b-icon icon="close" />
      </div>
    </header>

    <div class="modal-content-container">
      <section class="spacing-p-10">
        <b-field
          :label="
            unarchived ? General.REASON_UNARCHIVE : General.REASON_ARCHIVE
          "
          customClass="has-text-color-7"
          class="column is-6 mb-0 pb-0"
        >
          <b-select
            v-if="!unarchived"
            v-model="reason_option"
            :placeholder="General.TEXTS.SELECT_REASON_ARCHIVED"
          >
            <option
              v-for="justification in getJustificationList"
              :key="justification[0]"
              :value="justification[0]"
            >
              {{ justification[1] }}
            </option>
          </b-select>
        </b-field>
        <b-field class="column is-12 mb-0 pb-0">
          <template slot="label">
            <div
              v-if="!unarchived"
              class="has-text-color-7 is-6 has-text-weight-semibold is-flex is-align-items-center"
            >
              {{ General.NOTES }}
              <span class="optional-label ml-5">{{ General.OPTIONAL }}</span>
            </div>
          </template>
          <b-input
            v-model="message"
            maxlength="200"
            type="textarea"
            :placeholder="
              unarchived
                ? General.TEXTS.DETAIL_REASON_UNARCHIVED
                : General.TEXTS.DETAIL_REASON_ARCHIVED
            "
          ></b-input>
        </b-field>
        <div class="is-size-7">
          <table
            v-if="unarchived"
            class="table has-text-black"
            :title="General.TEXTS.PROCESS_LIST"
            :aria-describedby="General.TEXTS.PROCESS_LIST"
          >
            <thead>
              <tr>
                <th class="has-text-color-8 is-size-7">{{ General.NUP }}</th>
                <th class="has-text-color-8 is-size-7">
                  {{ General.SUBJECT }}
                </th>
                <th class="has-text-color-8 is-size-7">
                  {{ General.UNITY }}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="process in listProcess" :key="process.id">
                <td class="has-text-color-8">
                  {{ applyMaskNUP(process.nup) }}
                </td>
                <td class="has-text-color-8">{{ process.subject_name }}</td>
                <td class="has-text-color-8">
                  {{
                    `${process.capacity.entity.abbreviation}/${
                      process.capacity.abbreviation.includes("/")
                        ? process.capacity.abbreviation.split("/")[1]
                        : process.capacity.abbreviation
                    } - ${process.capacity.description}`
                  }}
                </td>
              </tr>
            </tbody>
          </table>
          <ListAttachments
            v-else
            v-model="currentPageNoEditable"
            class="column custom-width"
            :processNup="listProcess[0].nup"
            :canSign="false"
            :isArchived="true"
            :showModalUpload="showModalUpload"
            :showPDF="true"
          />
        </div>
        <div v-if="isProcessPortal && !unarchived" class="column is-flex">
          <b-checkbox
            v-model="lgpd"
            :type="isContrast ? 'is-white' : 'is-green-lawn'"
            class="has-text-color-7 is-align-items-flex-start mb-20"
          >
            {{ App.ModalArchive.PORTAL.CHECKBOX }}
          </b-checkbox>
        </div>
        <div v-if="processInPerson && !unarchived" class="column is-flex">
          <b-checkbox
            v-model="inPerson"
            :type="isContrast ? 'is-white' : 'is-green-lawn'"
            class="has-text-color-7 is-align-items-flex-start mb-20"
          >
            {{ App.ModalArchive.IN_PERSON.CHECKBOX }}
          </b-checkbox>
        </div>
      </section>

      <footer class="modal-card-foot is-justify-content-flex-end">
        <div class="buttons">
          <b-tooltip
            :active="hasUploadFiles"
            :label="General.TEXTS.WAIT_FOR_ALL_FILES_TO_LOAD"
            :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
            position="is-top"
            role="tooltip"
            class="mr-5"
          >
            <b-button
              :type="isContrast ? 'is-white-dark' : 'is-primary'"
              :disabled="hasUploadFiles"
              @click="confirmAction"
            >
              {{
                (unarchived ? General.UNARCHIVE : General.ARCHIVE).toUpperCase()
              }}
            </b-button>
          </b-tooltip>
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            class="is-outlined"
            @click="$emit('close')"
          >
            {{ General.CANCEL.toUpperCase() }}
          </b-button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { NUP, applyMask } from "@/utils/protocols";

import { App, General } from "@/utils/constants";
import ModalAttachments from "@/views/App/ModalAttachments";
import ListAttachments from "@/components/ListAttachments";
import { ATTACHEMENT_MAX_COUNT } from "@/utils/enumerate";

export default {
  name: "ModalArchive",
  components: {
    ListAttachments
  },
  props: {
    unarchived: {
      type: Boolean,
      default: false
    },
    listProcess: {
      type: Array,
      required: true
    },
    clearSelection: {
      type: Function,
      default: () => null
    },
    onClose: {
      type: Function,
      default: () => null
    }
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      reason_option: null,
      message: "",
      currentPageNoEditable: 1,
      lgpd: false,
      inPerson: false
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      getJustificationList: "getJustificationList",
      getProcessFileSize: "getProcessFileSize",
      getDraftFilesSize: "getDraftFilesSize",
      socketGetDocumentToUpdate: "socketGetDocumentToUpdate",
      needUpdateAttachments: "needUpdateAttachments",
      getListUploadFiles: "getListUploadFiles",
      isContrast: "isContrast"
    }),

    optionsReason() {
      return Object.entries(this.App.ModalArchive.REASONS);
    },

    isProcessPortal() {
      return this.listProcess[0]?.origin?.id === -1;
    },

    processInPerson() {
      return this.listProcess[0]?.in_person_bidding;
    },

    getCurrentSizeProcess() {
      return (
        this.getDraftFilesSize("processAttachmentFiles") +
        this.getProcessFileSize
      );
    },

    archiveProcessId() {
      return this.listProcess[0].id;
    },

    hasUploadFiles() {
      return this.getListUploadFiles.some(
        file =>
          file.process_id === this.listProcess[0].id &&
          !file.isErro &&
          file.status === "pending"
      );
    }
  },

  watch: {
    async currentPageNoEditable() {
      this.isLoading = true;
      await this.loadProcessFile();
      this.isLoading = false;
    },

    socketGetDocumentToUpdate: {
      async handler(value) {
        if (!this.unarchived && value.files_id?.length) {
          await this.requestProcessFiles(value);
          this.clearDocumentToUpdate();
        }
      },
      deep: true
    },

    needUpdateAttachments(value) {
      if (
        !this.unarchived &&
        value.fileObject.process_id === this.archiveProcessId
      )
        this.updateListAttachmentsAction(value);
    }
  },

  async beforeMount() {
    this.isLoading = true;
    if (!this.unarchived)
      await Promise.allSettled([
        this.fileTypesRequest({ state: true }),
        this.justificationListRequest(),
        this.loadProcessFile(),
        this.getAllowedExtensions(),
        this.requestProcessFileSize({ processId: this.archiveProcessId })
      ]);
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      processFileRequest: "getListAttachmentRequest",
      setArchive: "processArchiveUpdate",
      justificationListRequest: "archivesJustificationListRequest",
      updateListAttachmentsAction: "updateListAttachmentsAction",
      requestProcessFiles: "processFilesFilteredRequest",
      fileTypesRequest: "fileTypesRequest",
      requestProcessFileSize: "requestProcessFileSize",
      getAllowedExtensions: "getAllowedExtensions"
    }),

    ...mapMutations({
      clearDocumentToUpdate: "clearDocumentToUpdate"
    }),

    async confirmAction() {
      if (!this.unarchived && this.isProcessPortal && !this.lgpd) {
        this.snackToast.open({
          message: this.App.ModalArchive.PORTAL.WARNING,
          type: "is-warning",
          duration: 8000
        });
        return;
      }

      if (!this.unarchived && this.processInPerson && !this.inPerson) {
        this.snackToast.open({
          message: this.App.ModalArchive.IN_PERSON.WARNING,
          type: "is-warning",
          duration: 8000
        });
        return;
      }

      if (this.unarchived ? this.message : this.reason_option) {
        this.isLoading = true;

        const list = this.listProcess.map(process => ({
          process_id: process.id,
          is_archived: !this.unarchived,
          justification: this.message,
          option: this.reason_option,
          lgpd: this.lgpd || undefined
        }));

        const capacity = this.listProcess[0].capacity.abbreviation;

        const msg_success = (this.unarchived
          ? this.General.TEXTS.PROCESS_UNARCHIVED_SUCCESS
          : this.General.TEXTS.PROCESS_ARCHIVED_SUCCESS
        ).replace("(CAPACITIES)", capacity);

        await this.setArchive({ list, msg_success });
        this.isLoading = false;
        this.close();
        this.onClose();
      } else {
        this.snackToast.open({
          message: this.unarchived
            ? this.General.TEXTS.INSERT_REASON_DESARCHIVE
            : this.General.TEXTS.INSERT_REASON_ARCHIVE,
          type: "is-danger",
          duration: 8000
        });
      }
    },

    close() {
      this.clearSelection();
      this.$parent.close();
    },

    showModalUpload(isEditable = false, isPrimary = false) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalAttachments,
        props: {
          editable: isEditable,
          isPrimary: isPrimary,
          listProcess: this.listProcess,
          isArchived: true,
          currentSizeProcess: this.getCurrentSizeProcess
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    applyMaskNUP(value) {
      return applyMask(NUP, value);
    },

    async loadProcessFile() {
      await this.processFileRequest({
        processId: this.archiveProcessId,
        page: this.currentPageNoEditable,
        paginator_count: ATTACHEMENT_MAX_COUNT,
        type: "archive"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.modal-card {
  display: flex;
  justify-content: space-between;
  width: 60vw;
  max-width: 960px;
  max-height: 75vh;
  min-height: 500px;
  background-color: $color-1;

  @media only screen and (max-width: $tablet) {
    width: 100%;
  }

  .optional-label {
    font-size: 12px;
    font-weight: 300;
    color: $color-13;
  }
}

.header-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  border-bottom: 1px solid;
  border-bottom-color: #cdcdcd;
}

.card-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid;
  border-bottom-color: #cdcdcd;
}

.modal-content-container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
}

.custom-width {
  width: 98%;
  margin-bottom: 15px;
}

table tbody tr:hover {
  background-color: transparent !important;
}
</style>
